import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, ChevronLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { submitQuote } from "@/lib/api";
import { estimatePrice, isFormComplete } from "@/lib/pricing";
import PhotoPicker from "@/components/PhotoPicker";
import Footer from "@/components/Footer";

const TYPES = [
  "Déménagement",
  "Débarras de meubles",
  "Débarras de chantier",
  "Débarras de produits chimiques ou dangereux",
];
const VOLUMES = ["0 à 10 m³", "10 à 20 m³", "20 à 30 m³", "30 à 50 m³", "50 à 75 m³"];
const CARTONS = ["0 à 10", "10 à 25", "25 à 50", "50 à 100", "Plus de 100"];
const LOURDS = ["Piano", "Coffre-fort", "Billard", "Spa/Jacuzzi", "Machine professionnelle", "Aucun"];
const CRENEAUX = ["Matin", "Après-midi", "Soirée"];
const ETAGES = Array.from({ length: 16 }, (_, i) => String(i)); // 0..15
const COUNTS_9 = Array.from({ length: 10 }, (_, i) => String(i)); // 0..9
const COUNTS_7 = Array.from({ length: 8 }, (_, i) => String(i)); // 0..7

const STEPS = [
  { n: 1, label: "Introduction" },
  { n: 2, label: "Contact" },
  { n: 3, label: "Type d'intervention" },
  { n: 4, label: "Adresses & accès" },
  { n: 5, label: "Volume" },
  { n: 6, label: "Compléments" },
];

const initialData = {
  nom: "", prenom: "", telephone: "", email: "",
  adresse_facturation: "",
  type_intervention: "",
  // Chargement — champs séparés
  charg_rue: "", charg_cp: "", charg_ville: "", charg_pays: "France",
  // Livraison — champs séparés
  liv_rue: "", liv_cp: "", liv_ville: "", liv_pays: "France",
  // Combinés (envoyés au backend)
  adresse_chargement: "", adresse_livraison: "",
  distance_km: "",
  etage_chargement: "", ascenseur_chargement: "",
  etage_livraison: "", ascenseur_livraison: "",
  volume: "", appareils_plus_170: "", appareils_moins_170: "",
  cartons: "", objets_lourds: [], manutention_particuliere: "", manutention_combien: "",
  date_souhaitee: "", creneau: "", notes: "",
};

// Validators
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// Accepts: 0X XX XX XX XX, +33 X XX XX XX XX, with/without spaces/dots/dashes
const PHONE_RE = /^(?:(?:\+|00)33[\s.-]?|0)[1-9](?:[\s.-]?\d{2}){4}$/;

function joinAddress(rue, cp, ville, pays) {
  return [rue, cp, ville, pays].map((s) => (s || "").trim()).filter(Boolean).join(", ");
}

export default function Devis() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initialData);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [finalEstimate, setFinalEstimate] = useState(null);
  const [formStartedAt] = useState(() => Date.now());
  const [honeypot, setHoneypot] = useState("");
  const [photos, setPhotos] = useState([]);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleLourd = (val) => {
    setData((d) => {
      if (val === "Aucun") return { ...d, objets_lourds: d.objets_lourds.includes("Aucun") ? [] : ["Aucun"] };
      const list = d.objets_lourds.filter((x) => x !== "Aucun");
      return {
        ...d,
        objets_lourds: list.includes(val) ? list.filter((x) => x !== val) : [...list, val],
      };
    });
  };

  const validateStep = () => {
    if (step === 2) {
      if (!data.nom.trim() || !data.prenom.trim()) {
        toast.error("Merci de renseigner Nom et Prénom.");
        return false;
      }
      if (!data.telephone.trim()) {
        toast.error("Le téléphone est obligatoire.");
        return false;
      }
      if (!PHONE_RE.test(data.telephone.trim())) {
        toast.error("Numéro de téléphone invalide (ex: 07 53 22 15 29 ou +33 7 53 22 15 29).");
        return false;
      }
      if (!data.email.trim()) {
        toast.error("L'email est obligatoire.");
        return false;
      }
      if (!EMAIL_RE.test(data.email.trim())) {
        toast.error("Adresse email invalide.");
        return false;
      }
      if (!data.adresse_facturation.trim()) {
        toast.error("Merci de renseigner votre adresse de facturation.");
        return false;
      }
    }
    if (step === 3 && !data.type_intervention) {
      toast.error("Sélectionnez un type d'intervention.");
      return false;
    }
    if (step === 4) {
      if (!data.charg_rue.trim() || !data.charg_cp.trim() || !data.charg_ville.trim()) {
        toast.error("Merci de renseigner l'adresse de chargement complète (rue, code postal, ville).");
        return false;
      }
      // Si une adresse de livraison est partiellement remplie, exiger la ville au minimum
      const livFilled = data.liv_rue.trim() || data.liv_cp.trim() || data.liv_ville.trim();
      if (livFilled && (!data.liv_rue.trim() || !data.liv_cp.trim() || !data.liv_ville.trim())) {
        toast.error("Adresse de livraison incomplète (rue, code postal, ville).");
        return false;
      }
      if (livFilled && !data.ascenseur_livraison) {
        toast.error("Précisez s'il y a un ascenseur à l'adresse de livraison.");
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    if (step < 6) setStep(step + 1);
  };
  const back = () => step > 1 && setStep(step - 1);

  // Scroll back to top on step change
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [step, done]);

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const est = estimatePrice(data);
      const complete = isFormComplete(data);
      const payload = {
        ...data,
        adresse_chargement: joinAddress(data.charg_rue, data.charg_cp, data.charg_ville, data.charg_pays),
        adresse_livraison: joinAddress(data.liv_rue, data.liv_cp, data.liv_ville, data.liv_pays),
        price_estimate: complete ? est.lowEnd : null,
        website: honeypot,
        form_started_at: formStartedAt,
      };
      ["charg_rue","charg_cp","charg_ville","charg_pays","liv_rue","liv_cp","liv_ville","liv_pays"]
        .forEach((k) => delete payload[k]);
      if (!payload.email || !payload.email.trim()) delete payload.email;
      const res = await submitQuote(payload);
      if (complete) setFinalEstimate(est);
      setDone(true);
      toast.success(res.message || "Demande envoyée avec succès");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg || "Erreur de validation").join(", ")
          : "Erreur lors de l'envoi. Réessayez ou appelez-nous.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const progress = Math.round((step / 6) * 100);

  if (done) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
        <TopBar />
        <main className="flex-1 flex items-center justify-center px-6 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="devis-success"
            className="w-full max-w-2xl"
          >
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-black text-white inline-flex items-center justify-center mb-8">
                <Check size={28} strokeWidth={2} />
              </div>
              <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-6">
                Demande<br />
                <span className="text-[#FF2800]">enregistrée.</span>
              </h1>
              <p className="text-lg text-neutral-600 leading-relaxed mb-10">
                Merci {data.prenom}. Notre équipe vous recontacte sous 24 heures avec un
                devis détaillé. En cas d&apos;urgence : <a className="text-black font-semibold underline underline-offset-4" href="tel:0753221529">07 53 22 15 29</a>.
              </p>
            </div>

            {finalEstimate && (
              <motion.div
                data-testid="success-price-estimate"
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-2xl border border-black/10 bg-black text-white overflow-hidden mb-10"
              >
                <div className="p-8 md:p-10 text-center">
                  <div className="text-xs uppercase tracking-[0.3em] text-[#FFB800] mb-4">
                    Votre estimation
                  </div>
                  <div className="font-display text-5xl md:text-7xl font-light tracking-tighter">
                    à partir de <span className="text-[#FFB800]">{finalEstimate.lowEnd.toLocaleString("fr-FR")} €</span>
                  </div>
                  <div className="text-xs uppercase tracking-[0.2em] text-neutral-400 mt-4">HT</div>
                  <div className="text-sm text-neutral-300 mt-6 max-w-md mx-auto leading-relaxed">
                    Le <span className="text-white font-semibold">devis officiel</span>, détaillé et engagé, vous parvient par email sous 24h.
                  </div>
                </div>
              </motion.div>
            )}

            <div className="text-center">
              <button
                data-testid="back-home-btn"
                onClick={() => navigate("/")}
                className="btn-red px-7 py-4 rounded-full text-sm font-semibold"
              >
                Retour à l&apos;accueil
              </button>
            </div>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="devis-page" className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <TopBar />

      {/* Honeypot — hidden from humans, visible to bots */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
        <label>
          Ne pas remplir
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>
      </div>

      {/* Progress */}
      <div className="sticky top-16 z-40 bg-[#FAFAFA]/85 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.25em] text-neutral-500">
              Étape <span className="text-black font-semibold">{step}</span> / 6 — {STEPS[step - 1].label}
            </div>
            <div className="text-xs font-semibold text-[#FF2800]">{progress}%</div>
          </div>
          <div className="h-[3px] w-full bg-black/10 rounded-full overflow-hidden">
            <motion.div
              data-testid="progress-bar"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="h-full bg-[#FF2800]"
            />
          </div>
        </div>
      </div>

      <main className="flex-1 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {step > 1 && <PriceTeaser />}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              {step === 1 && <Step1 onStart={next} />}
              {step === 2 && <Step2 data={data} update={update} />}
              {step === 3 && <Step3 data={data} update={update} />}
              {step === 4 && <Step4 data={data} update={update} />}
              {step === 5 && <Step5 data={data} update={update} toggleLourd={toggleLourd} />}
              {step === 6 && <Step6 data={data} update={update} photos={photos} setPhotos={setPhotos} />}
            </motion.div>
          </AnimatePresence>

          {/* Nav */}
          {step > 1 && (
            <div className="mt-16 flex items-center justify-between border-t border-black/10 pt-8">
              <button
                data-testid="devis-back-btn"
                onClick={back}
                className="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-black transition"
              >
                <ArrowLeft size={16} /> Précédent
              </button>
              {step < 6 ? (
                <button
                  data-testid={`devis-next-btn-step-${step}`}
                  onClick={next}
                  className="btn-red inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-semibold"
                >
                  Continuer <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  data-testid="devis-submit-btn"
                  onClick={submit}
                  disabled={submitting}
                  className="btn-red inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-semibold disabled:opacity-60"
                >
                  {submitting ? (
                    <><Loader2 size={16} className="animate-spin" /> Envoi en cours…</>
                  ) : (
                    <>Envoyer ma demande <ArrowRight size={16} /></>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function TopBar() {
  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5 h-16 flex items-center">
      <div className="max-w-7xl w-full mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="devis-nav-home" className="inline-flex items-center gap-2 text-sm text-neutral-700 hover:text-black transition">
          <ChevronLeft size={16} /> Retour
        </Link>
        <Link to="/" data-testid="devis-nav-logo" className="flex items-center">
          <img src="/assets/logo-des-icon.png" alt="D.E.S" className="h-9 w-auto" />
        </Link>
        <a href="tel:0753221529" className="hidden sm:block text-sm font-medium text-neutral-700 hover:text-black">07 53 22 15 29</a>
      </div>
    </nav>
  );
}

/* ---------- Steps ---------- */

function StepHeader({ index, title, subtitle }) {
  return (
    <div className="mb-12">
      <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-4">Section {index} / 6</div>
      <h2 className="font-display text-4xl md:text-5xl font-light tracking-tighter leading-[1.05]">{title}</h2>
      {subtitle && <p className="mt-5 text-lg text-neutral-600 max-w-2xl leading-relaxed">{subtitle}</p>}
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="text-sm font-semibold text-black mb-2">
        {label} {required && <span className="text-[#FF2800]">*</span>}
      </div>
      {hint && <div className="text-xs text-neutral-500 mb-3 leading-relaxed">{hint}</div>}
      {children}
    </label>
  );
}

const inputCls =
  "w-full px-4 py-3.5 bg-white border border-black/15 rounded-xl text-base text-black placeholder-neutral-400 focus:outline-none focus:border-black focus:ring-2 focus:ring-black/10 transition";

function Step1({ onStart }) {
  return (
    <div data-testid="devis-step-1">
      <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-5">Devis · D.E.S Dépannage</div>
      <h1 className="font-display text-5xl md:text-7xl font-light tracking-tighter leading-[0.98] mb-8">
        Vous prévoyez<br />un déménagement ou<br /><span className="text-[#FF2800]">un débarras&nbsp;?</span>
      </h1>
      <p className="text-lg text-neutral-600 max-w-2xl leading-relaxed">
        Afin de vous proposer un devis précis et sans mauvaise surprise, nous avons besoin
        de quelques informations sur le volume, les accès et les éventuelles contraintes.
      </p>
      <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFB800]/15 text-black text-sm font-medium">
        <span className="w-2 h-2 rounded-full bg-[#FFB800]" /> Moins de 2 minutes
      </div>

      <div className="mt-16">
        <button
          data-testid="devis-start-btn"
          onClick={onStart}
          className="btn-red inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-semibold"
        >
          Commencer <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

function Step2({ data, update }) {
  return (
    <div data-testid="devis-step-2">
      <StepHeader index={2} title="Informations de contact" subtitle="Comment pouvons-nous vous joindre ?" />
      <div className="grid sm:grid-cols-2 gap-6">
        <Field label="Nom" required>
          <input data-testid="input-nom" className={inputCls} value={data.nom} onChange={(e) => update("nom", e.target.value)} placeholder="Dupont" />
        </Field>
        <Field label="Prénom" required>
          <input data-testid="input-prenom" className={inputCls} value={data.prenom} onChange={(e) => update("prenom", e.target.value)} placeholder="Jean" />
        </Field>
        <Field label="Téléphone" required hint="Ex: 07 53 22 15 29 ou +33 7 53 22 15 29">
          <input data-testid="input-telephone" type="tel" className={inputCls} value={data.telephone} onChange={(e) => update("telephone", e.target.value)} placeholder="07 53 22 15 29" />
        </Field>
        <Field label="Email" required>
          <input data-testid="input-email" type="email" className={inputCls} value={data.email} onChange={(e) => update("email", e.target.value)} placeholder="jean@example.com" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="🧾 Adresse de facturation" required hint="Adresse à inscrire sur votre devis et votre facture (souvent votre adresse personnelle ou de votre entreprise).">
            <input data-testid="input-adresse-facturation" className={inputCls} value={data.adresse_facturation} onChange={(e) => update("adresse_facturation", e.target.value)} placeholder="Numéro, rue, code postal, ville" />
          </Field>
        </div>
      </div>
    </div>
  );
}

function RadioCards({ name, options, value, onChange, columns = 2, testidPrefix }) {
  return (
    <div className={`grid grid-cols-1 ${columns === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"} gap-3`}>
      {options.map((opt) => {
        const selected = value === opt;
        return (
          <button
            key={opt}
            type="button"
            data-testid={`${testidPrefix}-${opt}`}
            onClick={() => onChange(opt)}
            className={`text-left px-5 py-4 rounded-xl border transition-all ${selected ? "bg-black text-white border-black" : "bg-white border-black/15 hover:border-black/40"}`}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium">{opt}</span>
              <span className={`w-4 h-4 rounded-full border-2 ${selected ? "border-white bg-[#FFB800]" : "border-black/30"}`} />
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Step3({ data, update }) {
  return (
    <div data-testid="devis-step-3">
      <StepHeader index={3} title="Type d'intervention" subtitle="Sélectionnez le service correspondant à votre besoin." />
      <RadioCards
        name="type_intervention"
        options={TYPES}
        value={data.type_intervention}
        onChange={(v) => update("type_intervention", v)}
        testidPrefix="type-intervention"
      />
    </div>
  );
}

function Step4({ data, update }) {
  return (
    <div data-testid="devis-step-4">
      <StepHeader index={4} title="Adresses & accès" subtitle="Précisez les conditions d'accès pour estimer la durée et le matériel nécessaire." />

      <div className="space-y-10">
        {/* Chargement */}
        <div className="rounded-2xl border border-black/10 bg-white p-6 md:p-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FF2800] mb-5 font-semibold">📦 Adresse de chargement</div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <Field label="Adresse (numéro et rue)" required>
                <input data-testid="input-charg-rue" className={inputCls} value={data.charg_rue} onChange={(e) => update("charg_rue", e.target.value)} placeholder="12 rue du Général de Gaulle" />
              </Field>
            </div>
            <Field label="Code postal" required>
              <input data-testid="input-charg-cp" className={inputCls} value={data.charg_cp} onChange={(e) => update("charg_cp", e.target.value)} placeholder="67000" />
            </Field>
            <Field label="Ville" required>
              <input data-testid="input-charg-ville" className={inputCls} value={data.charg_ville} onChange={(e) => update("charg_ville", e.target.value)} placeholder="Strasbourg" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Pays">
                <input data-testid="input-charg-pays" className={inputCls} value={data.charg_pays} onChange={(e) => update("charg_pays", e.target.value)} placeholder="France" />
              </Field>
            </div>
          </div>
        </div>

        {/* Livraison */}
        <div className="rounded-2xl border border-black/10 bg-white p-6 md:p-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FF2800] mb-2 font-semibold">📦 Adresse de livraison / dépôt</div>
          <div className="text-xs text-neutral-500 mb-5 leading-relaxed">
            Pour un débarras simple (sans relivraison ailleurs), laissez vide — nous nous occupons de l&apos;évacuation et de la mise en déchetterie.
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <Field label="Adresse (numéro et rue)">
                <input data-testid="input-liv-rue" className={inputCls} value={data.liv_rue} onChange={(e) => update("liv_rue", e.target.value)} placeholder="34 avenue de Colmar" />
              </Field>
            </div>
            <Field label="Code postal">
              <input data-testid="input-liv-cp" className={inputCls} value={data.liv_cp} onChange={(e) => update("liv_cp", e.target.value)} placeholder="67100" />
            </Field>
            <Field label="Ville">
              <input data-testid="input-liv-ville" className={inputCls} value={data.liv_ville} onChange={(e) => update("liv_ville", e.target.value)} placeholder="Strasbourg" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Pays">
                <input data-testid="input-liv-pays" className={inputCls} value={data.liv_pays} onChange={(e) => update("liv_pays", e.target.value)} placeholder="France" />
              </Field>
            </div>
          </div>
        </div>

        <Field label="📏 Distance entre les deux adresses">
          <input data-testid="input-distance" className={inputCls} value={data.distance_km} onChange={(e) => update("distance_km", e.target.value)} placeholder="ex. 15 km, ou laisser vide" />
        </Field>

        <div className="grid md:grid-cols-2 gap-8">
          <Field label="🏢 Étage — adresse de chargement" hint="0 = rez-de-chaussée">
            <SelectGrid options={ETAGES} value={data.etage_chargement} onChange={(v) => update("etage_chargement", v)} testidPrefix="etage-chargement" />
          </Field>
          <Field label="🛗 Ascenseur — chargement" hint="Assez grand pour meubles/électroménager ?">
            <RadioCards options={["Oui", "Non"]} value={data.ascenseur_chargement} onChange={(v) => update("ascenseur_chargement", v)} testidPrefix="ascenseur-chargement" />
          </Field>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <Field label="🏢 Étage — adresse de livraison" hint="0 = rez-de-chaussée">
            <SelectGrid options={ETAGES} value={data.etage_livraison} onChange={(v) => update("etage_livraison", v)} testidPrefix="etage-livraison" />
          </Field>
          <Field label="🛗 Ascenseur — livraison" hint="Assez grand pour meubles/électroménager ?">
            <RadioCards options={["Oui", "Non"]} value={data.ascenseur_livraison} onChange={(v) => update("ascenseur_livraison", v)} testidPrefix="ascenseur-livraison" />
          </Field>
        </div>
      </div>
    </div>
  );
}

function SelectGrid({ options, value, onChange, testidPrefix }) {
  return (
    <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
      {options.map((opt) => {
        const selected = value === opt;
        return (
          <button
            key={opt}
            type="button"
            data-testid={`${testidPrefix}-${opt}`}
            onClick={() => onChange(opt)}
            className={`h-10 rounded-lg text-sm font-medium border transition ${selected ? "bg-black text-white border-black" : "bg-white border-black/15 hover:border-black/40"}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function Step5({ data, update, toggleLourd }) {
  return (
    <div data-testid="devis-step-5">
      <StepHeader index={5} title="Volume à transporter" subtitle="Une estimation approximative suffit." />

      <div className="space-y-10">
        <Field label="📦 Volume estimé" hint="1 camionnette ≈ 10 m³ · 2 ≈ 20 m³ · 3 ≈ 30 m³">
          <RadioCards options={VOLUMES} value={data.volume} onChange={(v) => update("volume", v)} columns={3} testidPrefix="volume" />
        </Field>

        <div className="grid md:grid-cols-2 gap-8">
          <Field label="📏 Électroménagers > 1,70 m" hint="Réfrigérateur, congélateur, armoire…">
            <SelectGrid options={COUNTS_9} value={data.appareils_plus_170} onChange={(v) => update("appareils_plus_170", v)} testidPrefix="app-plus170" />
          </Field>
          <Field label="📏 Électroménagers < 1,70 m">
            <SelectGrid options={COUNTS_9} value={data.appareils_moins_170} onChange={(v) => update("appareils_moins_170", v)} testidPrefix="app-moins170" />
          </Field>
        </div>

        <Field label="📦 Cartons">
          <RadioCards options={CARTONS} value={data.cartons} onChange={(v) => update("cartons", v)} columns={3} testidPrefix="cartons" />
        </Field>

        <Field label="⚖️ Objets très lourds" hint="Sélectionnez tout ce qui s'applique">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {LOURDS.map((opt) => {
              const sel = data.objets_lourds.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  data-testid={`lourd-${opt}`}
                  onClick={() => toggleLourd(opt)}
                  className={`px-4 py-3 rounded-xl border text-left transition ${sel ? "bg-black text-white border-black" : "bg-white border-black/15 hover:border-black/40"}`}
                >
                  <span className="text-sm font-medium">{opt}</span>
                </button>
              );
            })}
          </div>
        </Field>

        <div className="grid md:grid-cols-2 gap-8">
          <Field label="⚠️ Manutention particulière" hint="Verre, miroir, marbre, œuvre d'art, aquarium…">
            <RadioCards options={["Oui", "Non"]} value={data.manutention_particuliere} onChange={(v) => update("manutention_particuliere", v)} testidPrefix="manutention" />
          </Field>
          {data.manutention_particuliere === "Oui" && (
            <Field label="Si oui, combien d'objets ?">
              <SelectGrid options={COUNTS_7.slice(1)} value={data.manutention_combien} onChange={(v) => update("manutention_combien", v)} testidPrefix="manutention-nb" />
            </Field>
          )}
        </div>
      </div>
    </div>
  );
}

function Step6({ data, update, photos, setPhotos }) {
  return (
    <div data-testid="devis-step-6">
      <StepHeader index={6} title="Informations complémentaires" subtitle="Dernière étape — précisez vos préférences." />
      <div className="space-y-8">
        <div className="grid md:grid-cols-2 gap-6">
          <Field label="📅 Date souhaitée">
            <input data-testid="input-date" type="date" className={inputCls} value={data.date_souhaitee} onChange={(e) => update("date_souhaitee", e.target.value)} />
          </Field>
          <Field label="🕒 Créneau préféré">
            <RadioCards options={CRENEAUX} value={data.creneau} onChange={(v) => update("creneau", v)} columns={3} testidPrefix="creneau" />
          </Field>
        </div>

        <Field label="📝 Notes et remarques" hint="Contraintes d'accès, horaires particuliers, objets spécifiques…">
          <textarea data-testid="input-notes" rows={6} className={inputCls} value={data.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Précisions utiles pour préparer l'intervention…" />
        </Field>

        <PhotoPicker photos={photos} setPhotos={setPhotos} />

        <motion.div
          data-testid="step6-teaser"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="rounded-2xl border border-dashed border-black/15 bg-[#FAFAFA] p-6 text-center"
        >
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FF2800] mb-2">
            <Sparkles size={14} />
            Presque terminé
          </div>
          <div className="text-base text-neutral-700 font-medium">
            Votre estimation s&apos;affichera juste après l&apos;envoi de la demande.
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function PriceTeaser() {
  return (
    <motion.div
      data-testid="price-teaser-banner"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-12 relative overflow-hidden rounded-2xl border border-black/10 bg-gradient-to-r from-black via-neutral-900 to-black text-white"
    >
      {/* Animated shimmer */}
      <motion.div
        aria-hidden
        initial={{ x: "-100%" }}
        animate={{ x: "200%" }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
        className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/10 to-transparent"
      />
      <div className="relative flex flex-wrap items-center justify-between gap-4 px-6 md:px-8 py-5">
        <div className="flex items-center gap-4">
          <div className="relative flex items-center justify-center w-10 h-10 rounded-full bg-[#FF2800]/15 border border-[#FF2800]/30">
            <Sparkles size={16} className="text-[#FFB800]" />
            <span className="absolute inset-0 rounded-full border border-[#FFB800]/40 animate-ping" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#FFB800] mb-1">
              Estimation automatique
            </div>
            <div className="text-sm md:text-base text-white font-medium">
              Votre prix indicatif sera révélé à la fin du questionnaire.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-neutral-300">
          <Loader2 size={14} className="animate-spin text-[#FF2800]" />
          <span>Calcul en cours…</span>
        </div>
      </div>
    </motion.div>
  );
}

